import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from "react";


interface WorkflowSelectionContextType {
  selectedIds: Set<string>;
  toggleSelection: (workflowId: string) => void;
  selectAll: (workflowIds: string[]) => void;
  clearSelection: () => void;
  isSelected: (workflowId: string) => boolean;
}


const WorkflowSelectionContext = createContext<WorkflowSelectionContextType | undefined>(undefined);


export function WorkflowSelectionProvider({ children }: { children: ReactNode }) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleSelection = useCallback((workflowId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(workflowId)) {
        next.delete(workflowId);
      } else {
        next.add(workflowId);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback((workflowIds: string[]) => {
    setSelectedIds(new Set(workflowIds));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const isSelected = useCallback(
    (workflowId: string) => selectedIds.has(workflowId),
    [selectedIds],
  );

  const value = useMemo(
    () => ({
      selectedIds,
      toggleSelection,
      selectAll,
      clearSelection,
      isSelected,
    }),
    [selectedIds, toggleSelection, selectAll, clearSelection, isSelected],
  );

  return (
    <WorkflowSelectionContext.Provider value={value}>
      {children}
    </WorkflowSelectionContext.Provider>
  );
}


export function useWorkflowSelection() {
  const context = useContext(WorkflowSelectionContext);
  if (!context) {
    throw new Error("useWorkflowSelection must be used within WorkflowSelectionProvider");
  }
  return context;
}
